import { useState } from 'react'
import { Brand } from '../../../shared/components/Brand'
import { Game24Demo } from './Game24Demo'

// Landing page. Two clearly separated parts: (1) the 24-game objective with an animated
// demo, (2) how every nerdle labs game works (voice-activated). Then explicit, opt-in
// consent + start. Declining routes to a silent-play message (Phase-2 fallback).
export const ConsentScreen = ({ onConsent }: { onConsent: () => void }) => {
  const [checked, setChecked] = useState(false)
  // The privacy supplement is a static page in the labs app root (sibling of index.html).
  const privacyHref =
    typeof window !== 'undefined'
      ? window.location.pathname.replace(/\/[^/]*$/, '') + '/privacy.html'
      : 'privacy.html'

  return (
    <div className="flex flex-col items-center px-6 pt-10 pb-6">
      <div className="w-full max-w-lg text-center">
        <Brand game="twentyfour" />

        {/* ---- The game ---- */}
        <h1 className="mt-8 text-xl font-semibold text-gray-800">Make 24 using all 4 tiles</h1>
        <p className="mt-1 text-sm text-gray-500">Combine them with + − × ÷ — each tile used once.</p>

        <div className="mt-6">
          <Game24Demo />
        </div>

        {/* ---- How nerdle labs games work ---- */}
        <h2 className="mt-10 text-base font-semibold text-gray-800">How to play nerdle labs games</h2>
        <p className="mt-2 text-gray-600 leading-relaxed">
          Every nerdle labs game is voice-activated — talk out loud as you solve, so we can see how you
          think. Keep talking to reveal the tiles, and say <span className="font-medium">“commit”</span>{' '}
          (or tap the button) when you’re done.
        </p>

        {/* ---- Consent ---- */}
        <p className="mt-4 text-xs text-gray-400">
          We record your voice as text only — your browser transcribes it; no audio is kept.
        </p>

        <label className="mt-5 flex items-start gap-3 cursor-pointer rounded-lg border border-gray-200 p-4 text-left hover:bg-gray-50">
          <input
            type="checkbox"
            className="mt-1 h-5 w-5 accent-[#820458]"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
          />
          <span className="text-sm text-gray-700">
            I confirm I am 18 or over and consent to my voice being recorded, transcribed to text, and the resulting transcripts being
            used for gaming and research purposes. I understand no audio is retained.{' '}
            <a
              href={privacyHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="underline hover:text-[#820458]"
            >
              More details.
            </a>
          </span>
        </label>

        <button
          type="button"
          disabled={!checked}
          onClick={onConsent}
          className="mt-5 w-full rounded-lg bg-[#820458] py-3 text-white font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Start the 24-game
        </button>
        <p className="mt-2 text-xs text-gray-400">Your browser will ask for microphone access next.</p>
        <p className="mt-3 text-xs text-gray-400">
          <a href={privacyHref} className="underline hover:text-[#820458]">
            Privacy policy
          </a>
        </p>
      </div>
    </div>
  )
}
